/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../util/reactIntl';
import { LISTING_STATE_DRAFT, propTypes } from '../../../util/types';
import { createSlug } from '../../../util/urlHelpers';

import { ensureCurrentUser } from '../../../util/data';

import { AvatarLarge, ExternalLink, IconShare, InlineTextButton, LinkedLogo, NamedLink, NotificationBadge } from '../../../components';

import css from './TopbarMobileMenu.module.css';
import requestformLogo from '../../../assets/images/requestform-logo.png';

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserListings,
    currentUserHasListings,
    currentUser,
    notificationCount,
    onLogout,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const { accountType, isAdminAccount } = (currentUser && currentUser.id && currentUser.attributes.profile.publicData) || {};
  const { currentSeeds = 0, CurrentTokens = 0 } = (currentUser && currentUser.id && currentUser.attributes.profile.protectedData) || {};
  const CurrentSeeds = currentSeeds || CurrentTokens;
  const isCompany = accountType ? accountType == 'company' : true;
  const currentUserHasPublishedListing = currentUserListings && currentUserListings.length
    ? currentUserListings[0].attributes.state !== LISTING_STATE_DRAFT
    : false;

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div>
          <div className={css.content2}>
            <LinkedLogo format={'mobile'} alt="site-logo" className={css.logoLink} />
            {/* <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div> */}
            {login}
          </div>
          {isCompany
            ? null
            : currentUserListings && currentUserListings.length
              ? <NamedLink
                name={currentUserHasPublishedListing ? "ListingPage" : "EditListingPage"}
                className={css.createNewListingLink}
                params={{
                  id: currentUserListings[0].id.uuid,
                  slug: createSlug(currentUserListings[0].attributes.title),
                  type: currentUserHasPublishedListing ? 'edit' : 'draft',
                  tab: 'what-say'
                }}
              >
                <FormattedMessage id="TopbarMobileMenu.editListing" />
              </NamedLink>
              : <NamedLink className={css.createNewListingLink} name={"NewListingPage"}>
                <FormattedMessage id="TopbarMobileMenu.createListing" />
              </NamedLink>}

          <div className={css.menuLinks}>
            <NamedLink className={css.navLink} name="CMSPage" params={{ pageId: 'about' }}>
              <FormattedMessage id="TopbarDesktop.about" />
            </NamedLink>
            <NamedLink className={css.navLink} name="CMSPage" params={{ pageId: 'for-companies' }}>
              <FormattedMessage id="Forteh For Companies" />
            </NamedLink>
            {/* <NamedLink className={css.navLink} name="LandingPage">
              <FormattedMessage id="TopbarDesktop.communityPage" />
            </NamedLink> */}
            <NamedLink className={css.navLink} name="CMSPage" params={{ pageId: 'fractional-pros' }}>
              <FormattedMessage id="Forteh For Fractional Pros" />
            </NamedLink>
            <NamedLink className={css.navLink} name="CMSPage" params={{ pageId: 'your-forte' }}>
              <FormattedMessage id="General FAQs" />
            </NamedLink>
            <NamedLink className={css.navLink} name="CMSPage" params={{ pageId: 'how-it-works' }}>
              <FormattedMessage id="How Forteh Works" />
            </NamedLink>
            <NamedLink className={css.navLink} name="BlogPage">
              <FormattedMessage id="Blog" />
            </NamedLink>
          </div>
        </div>
        <div className={css.socialMediaLinks}>
          {/* <div className={css.socialMediaIcons} id="facebook"><ExternalLink href="https://www.facebook.com/Sharetribe/" target="_blank">
            <IconShare type='facebook' />
          </ExternalLink></div> */}
          <div className={css.socialMediaIcons} id="linkedin"><ExternalLink href="https://linkedin.com/sharetribe" target="_blank">
            <IconShare type='linkedin' />
          </ExternalLink></div>
          <div className={css.socialMediaIcons} id="instagram"><ExternalLink href="https://www.instagram.com/@Sharetribe" target="_blank">
            <IconShare type='instagram' />
          </ExternalLink></div>
        </div>
        {/* <div className={css.footer}>
        
        </div> */}
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <div className={css.root}>
      <div className={css.modalContent}>
        <LinkedLogo format={'mobile'} alt="site-logo" className={css.logoLink} />
        <AvatarLarge className={css.avatar} user={currentUser} />
        <div className={css.content}>
          <span className={css.greeting}>
            <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
          </span>

          <NamedLink
            className={classNames(css.inbox, currentPageClass('InboxPage'))}
            name="InboxPage"
            params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>
          {isCompany
            ? <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
            </NamedLink>
            : null}
          {/* <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink> */}

          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="Step1Page"
          >
            <FormattedMessage id="Edit Profile" />
          </NamedLink>

          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
          {/* <div className={css.spacer} /> */}
        </div>


        {!isAdminAccount ?   isCompany
          ? <NamedLink className={css.createNewListingLink} name={"NewCompanyListingPage"}>
            <FormattedMessage id="TopbarMobileMenu.createCompanyListing" />
          </NamedLink>
          : (currentUserListings && currentUserListings.length > 0)
            ? <NamedLink
              name={currentUserHasPublishedListing ? "ListingPage" : "EditListingPage"}
              className={css.createNewListingLink}
              params={{
                id: currentUserListings[0].id.uuid,
                slug: createSlug(currentUserListings[0].attributes.title),
                type: currentUserHasPublishedListing ? 'edit' : 'draft',
                tab: 'pricing'
              }}
            >
              <FormattedMessage id="TopbarMobileMenu.editListing" />
            </NamedLink>
            : <NamedLink className={css.createNewListingLink} name={"NewListingPage"}>
              <FormattedMessage id="TopbarMobileMenu.createListing" />
            </NamedLink> : null}

        {
          !isCompany ?
            <NamedLink className={css.discoverTalentLink} name="AlgoliaSearchPage" >
              <FormattedMessage id="TopbarDesktop.roles" />
            </NamedLink>
            : <NamedLink name="AlgoliaSearchPage" className={css.discoverTalentLink}>
              <span className={css.createListing}>Discover Talent</span>
            </NamedLink>
        }
        {isCompany && <NamedLink className={classNames(css.navLink, css.coinWrapper)} name="PurchaseAcornsPage">
          <span className={css.logoWrapper}><img src={requestformLogo} /></span>
          <span className={classNames(css.coins, CurrentSeeds > 9 ? css.Largecoins : null)}>
            {Number(CurrentSeeds) || 0}
          </span>
        </NamedLink>}

        <div className={css.menuLinks} style={{ marginTop: 0 }}>
          <NamedLink className={css.navLink} name="CMSPage" params={{ pageId: 'about' }}>
            <FormattedMessage id="TopbarDesktop.about" />
          </NamedLink>
          <NamedLink className={css.navLink} name="CMSPage" params={{ pageId: 'how-it-works' }}>
            <FormattedMessage id="How Forteh Works" />
          </NamedLink>
          <NamedLink className={css.navLink} name="BlogPage">
            <FormattedMessage id="Blog" />
          </NamedLink>
          {/* <NamedLink className={css.navLink} name="CMSPage" params={{ pageId: 'solutions' }}>
            <FormattedMessage id="TopbarDesktop.solutions" />
          </NamedLink> */}
          {/* <NamedLink className={css.navLink} name="LandingPage">
            <FormattedMessage id="TopbarDesktop.communityPage" />
          </NamedLink> */}
          {/* <NamedLink className={css.navLink} name="LandingPage">
            <FormattedMessage id="TopbarDesktop.resources" />
          </NamedLink> */}
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <FormattedMessage id="TopbarMobileMenu.logoutLink" />
          </InlineTextButton>
        </div>
      </div>
      <div className={css.socialMediaLinks}>
        {/* <div className={css.socialMediaIcons} id="facebook"><ExternalLink href="https://www.facebook.com/Sharetribe/" target="_blank">
          <IconShare type='facebook' />
        </ExternalLink></div> */}
        {/* <div className={css.socialMediaIcons} id="linkedin"><ExternalLink href="https://linkedin.com/sharetribe" target="_blank">
          <IconShare type='linkedin' />
        </ExternalLink></div>
        <div className={css.socialMediaIcons} id="instagram"><ExternalLink href="https://www.instagram.com/@Sharetribe" target="_blank">
          <IconShare type='instagram' />
          
        </ExternalLink></div> */}
        <div className={css.socialMediaIcons} id="linkedin"><ExternalLink href="https://www.linkedin.com/company/fortehbymetier/" target="_blank">
          <IconShare type='linkedin' />
        </ExternalLink></div>
        <div className={css.socialMediaIcons} id="instagram"><ExternalLink href="https://www.instagram.com/fortehbymetier/" target="_blank">
          <IconShare type='instagram' />
        </ExternalLink></div>
      </div>
      {/* <div className={css.footer}>
      </div> */}
    </div>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, notificationCount: 0, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
